import { supabase } from '../config/supabase';
import type { UserPreferences } from '../types';
import { formatPlayerName } from './playerPreferences';
import { isParticipationCancellation } from './participationEventSemantics';
import { isDesignatedSubstitute, type RosterRole } from './rosterRoles';

export type ParticipationDetail = {
  id: string;
  matchDate: string;
  matchSiteType: string;
  opponent: string;
  lineAssignment: string;
  participationStatus: 'Played' | 'Late Cancel' | 'Late Substitute' | 'Standby Promotion' | 'Future Assignment';
  cancellationReason: string;
  replacementDetails: string;
};

export type ParticipationReportRow = {
  playerId: string;
  playerName: string;
  firstName: string;
  lastName: string;
  sex: 'male' | 'female' | string | null;
  targetMatches: number;
  adjustedTarget: number;
  totalPlayed: number;
  countedPlayed: number;
  provisionalPlayed: number;
  lateCancels: number;
  lateSubstitutes: number;
  playedRegular: number;
  singlesPlayed: number;
  doublesPlayed: number;
  futureSingles: number;
  futureDoubles: number;
  totalFuture: number;
  statusDelta: number;
  lastPlayedDate: string | null;
  lastPlayedWeeksAgo: number | null;
  nextMatchDate: string | null;
  nextMatchWeeksAway: number | null;
  homeMatches: number;
  awayMatches: number;
  neutralMatches: number;
  unknownSiteMatches: number;
  totalCompletedAndFutureScheduledMatches: number;
  homeMatchPercentage: number | null;
  details: ParticipationDetail[];
  calculation: {
    lateCancellationAdjustment: number;
    otherAllocationAdjustment: number;
    lateSubstitutionAdjustment: number;
    adjustedParticipation: number;
  };
  plannedAssignments: Array<{ matchId: string; credits: number; lineupStatus: 'Draft' | 'Published' }>;
  plannedPublished: number;
  plannedDraft: number;
};

type ParticipationRosterRow = {
  player_id: string;
  target_match_count: number | null;
  player_tier: string | null;
  roster_status: string | null;
  roster_role: RosterRole | string | null;
  is_active: boolean;
};

type ParticipationPlayerRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  sex: 'male' | 'female' | null;
  ntrp_rating: number | null;
  is_active: boolean;
};

type MatchRow = {
  id: string;
  match_status: string;
  lineup_status: string;
  match_date: string;
  match_site_type: string | null;
  opponent_name: string | null;
};

type ParticipationEventRow = {
  match_event_id: string;
  player_id: string;
  league_configuration_position_id: string | null;
  participation_event_type: string;
  allocation_count_indicator: boolean;
  manual_allocation_override_indicator?: boolean;
  participation_event_reason: string | null;
  participation_event_notes: string | null;
  replaced_player_id: string | null;
  replacement_player_id: string | null;
};

type LineupEntryRow = {
  match_event_id: string;
  player_id: string;
  league_configuration_position_id: string;
};

type LineResultRow = {
  match_event_id: string;
  league_configuration_position_id: string;
  result_value: string | null;
};

type PositionRow = {
  id: string;
  position_code: string;
  line_type: string;
};

export type LoadParticipationReportRowsOptions = {
  teamId: string;
  seasonId?: string;
  includeInactivePlayers: boolean;
  includeSubstitutePlayers: boolean;
  userPreferences: UserPreferences;
  playerLabel?: (playerId: string) => string;
};

function wholeWeeksBetween(fromDate: string, toDate: string): number {
  const [fromYear, fromMonth, fromDay] = fromDate.split('-').map(Number);
  const [toYear, toMonth, toDay] = toDate.split('-').map(Number);
  const from = Date.UTC(fromYear, fromMonth - 1, fromDay);
  const to = Date.UTC(toYear, toMonth - 1, toDay);
  return Math.max(0, Math.floor((to - from) / (7 * 24 * 60 * 60 * 1000)));
}

export async function loadParticipationReportRows({
  teamId,
  seasonId,
  includeInactivePlayers,
  includeSubstitutePlayers,
  userPreferences,
  playerLabel,
}: LoadParticipationReportRowsOptions): Promise<{ rows: ParticipationReportRow[]; seasonId: string; nextBoundaryAt: string | null }> {
  let selectedSeasonId = seasonId || '';
  if (!selectedSeasonId) {
    const teamResult = await supabase.from('teams').select('season_id').eq('id', teamId).maybeSingle();
    if (teamResult.error) throw new Error(teamResult.error.message);
    selectedSeasonId = (teamResult.data as { season_id?: string } | null)?.season_id || '';
  }
  if (!selectedSeasonId) throw new Error('Selected team does not have a season.');

  const rosterResult = await supabase
    .from('team_rosters')
    .select('player_id, target_match_count, player_tier, roster_status, roster_role, is_active')
    .eq('team_id', teamId)
    .is('deleted_at', null);

  if (rosterResult.error) throw new Error(rosterResult.error.message);

  const rosterRows = ((rosterResult.data || []) as unknown as ParticipationRosterRow[]).filter((row) => {
    if (!includeInactivePlayers && (!row.is_active || row.roster_status === 'inactive')) return false;
    if (!includeSubstitutePlayers && (row.roster_status === 'substitute' || isDesignatedSubstitute(row))) return false;
    return true;
  });
  const rosterPlayerIds = rosterRows.map((row) => row.player_id);
  if (rosterPlayerIds.length === 0) return { rows: [], seasonId: selectedSeasonId, nextBoundaryAt: null };

  const [playersResult, matchesResult] = await Promise.all([
    supabase.from('players').select('id, first_name, last_name, sex, ntrp_rating, is_active').in('id', rosterPlayerIds).is('deleted_at', null),
    supabase
      .from('match_events')
      .select('id, match_status, lineup_status, match_date, match_site_type, opponent_name')
      .eq('team_id', teamId)
      .eq('season_id', selectedSeasonId)
      .is('deleted_at', null),
  ]);

  if (playersResult.error || matchesResult.error) throw new Error(playersResult.error?.message || matchesResult.error?.message || 'Unable to load participation records.');

  const playerRows = ((playersResult.data || []) as unknown as ParticipationPlayerRow[]).filter((player) => includeInactivePlayers || player.is_active);
  const playerById = new Map(playerRows.map((player) => [player.id, player]));
  const visibleRosterRows = rosterRows.filter((row) => playerById.has(row.player_id));
  const matches = (matchesResult.data || []) as unknown as MatchRow[];
  const nonCancelledMatchIds = matches.filter((match) => match.match_status !== 'Cancelled').map((match) => match.id);
  const today = new Date().toISOString().slice(0, 10);
  const occurrenceResult = matches.length > 0
    ? await supabase.rpc('match_occurrence_states', { p_match_ids: matches.map((match) => match.id) })
    : { data: [], error: null };
  if (occurrenceResult.error) throw new Error(occurrenceResult.error.message);
  const occurrenceRows = (occurrenceResult.data || []) as Array<{ match_event_id: string; occurrence_state: string; scheduled_end_at: string | null }>;
  const occurrenceByMatchId = new Map(
    occurrenceRows
      .map((row) => [row.match_event_id, row.occurrence_state]),
  );
  const nextBoundaryAt = occurrenceRows
    .filter((row) => ['Upcoming', 'In Progress'].includes(row.occurrence_state) && row.scheduled_end_at)
    .map((row) => row.scheduled_end_at as string)
    .sort()[0] || null;
  const pastMatchIds = new Set(matches.filter((match) => occurrenceByMatchId.get(match.id) === 'Past').map((match) => match.id));
  const futureMatchIds = matches
    .filter((match) => ['Upcoming', 'In Progress'].includes(occurrenceByMatchId.get(match.id) || '')
      && ['Draft', 'Published', 'Locked'].includes(match.lineup_status || 'Not Started'))
    .map((match) => match.id);
  const allMatchIds = nonCancelledMatchIds;
  const visiblePlayerIds = visibleRosterRows.map((row) => row.player_id);

  const [playedEventsResult, scheduledEventsResult, cancellationEventsResult, replacementEventsResult, entriesResult, lineResultsResult, lineStatusesResult] = await Promise.all([
    nonCancelledMatchIds.length > 0
      ? supabase
          .from('player_participation_events')
          .select('match_event_id, player_id, league_configuration_position_id, participation_event_type, allocation_count_indicator, manual_allocation_override_indicator, participation_event_reason, participation_event_notes, replaced_player_id, replacement_player_id')
          .in('match_event_id', nonCancelledMatchIds)
          .in('player_id', visiblePlayerIds)
          .eq('participation_event_type', 'played')
          .is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
    nonCancelledMatchIds.length > 0
      ? supabase.from('player_participation_events')
          .select('match_event_id, player_id, league_configuration_position_id, participation_event_type, allocation_count_indicator, manual_allocation_override_indicator, participation_event_reason, participation_event_notes, replaced_player_id, replacement_player_id')
          .in('match_event_id', nonCancelledMatchIds)
          .in('player_id', visiblePlayerIds)
          .eq('participation_event_type', 'scheduled')
          .is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
    nonCancelledMatchIds.length > 0
      ? supabase
          .from('player_participation_events')
          .select('match_event_id, player_id, league_configuration_position_id, participation_event_type, allocation_count_indicator, manual_allocation_override_indicator, participation_event_reason, participation_event_notes, replaced_player_id, replacement_player_id, match_events!inner(team_id, season_id, match_status, deleted_at)')
          .in('match_event_id', nonCancelledMatchIds)
          .in('player_id', visiblePlayerIds)
          .in('participation_event_type', ['cancelled_early', 'cancelled_late', 'administrative_correction', 'administrative_adjustment'])
          .is('deleted_at', null)
          .is('match_events.deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
    nonCancelledMatchIds.length > 0
      ? supabase
          .from('player_participation_events')
          .select('match_event_id, player_id, league_configuration_position_id, participation_event_type, allocation_count_indicator, manual_allocation_override_indicator, participation_event_reason, participation_event_notes, replaced_player_id, replacement_player_id, match_events!inner(team_id, season_id, match_status, deleted_at)')
          .in('match_event_id', nonCancelledMatchIds)
          .in('player_id', visiblePlayerIds)
          .in('participation_event_type', ['replacement', 'late_substitute', 'standby_promotion'])
          .is('deleted_at', null)
          .is('match_events.deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
    allMatchIds.length > 0
      ? supabase
          .from('lineup_entries')
          .select('match_event_id, league_configuration_position_id, player_id')
          .in('match_event_id', allMatchIds)
          .in('player_id', visiblePlayerIds)
          .is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
    nonCancelledMatchIds.length > 0
      ? supabase
          .from('line_results')
          .select('match_event_id, league_configuration_position_id, result_value')
          .in('match_event_id', nonCancelledMatchIds)
          .is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
    nonCancelledMatchIds.length > 0
      ? supabase.from('lineup_position_statuses')
          .select('match_event_id, league_configuration_position_id, line_status')
          .in('match_event_id', nonCancelledMatchIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (playedEventsResult.error || scheduledEventsResult.error || cancellationEventsResult.error || replacementEventsResult.error || entriesResult.error || lineResultsResult.error || lineStatusesResult.error) {
    throw new Error(playedEventsResult.error?.message || scheduledEventsResult.error?.message || cancellationEventsResult.error?.message || replacementEventsResult.error?.message || entriesResult.error?.message || lineResultsResult.error?.message || lineStatusesResult.error?.message || 'Unable to load participation counts.');
  }

  const playedEvents = (playedEventsResult.data || []) as unknown as ParticipationEventRow[];
  const scheduledEvents = (scheduledEventsResult.data || []) as unknown as ParticipationEventRow[];
  const cancellationEvents = (cancellationEventsResult.data || []) as unknown as ParticipationEventRow[];
  const replacementEvents = (replacementEventsResult.data || []) as unknown as ParticipationEventRow[];
  const entries = (entriesResult.data || []) as unknown as LineupEntryRow[];
  const lineResults = (lineResultsResult.data || []) as unknown as LineResultRow[];
  const defaultedPositionKeys = new Set(
    lineResults
      .filter((result) => result.result_value === 'Default by Opponent' || result.result_value === 'Default by Us')
      .map((result) => `${result.match_event_id}:${result.league_configuration_position_id}`),
  );
  for (const status of (lineStatusesResult.data || []) as Array<{ match_event_id: string; league_configuration_position_id: string; line_status: string }>) {
    if (status.line_status === 'default_by_opponent' || status.line_status === 'default_by_us') {
      defaultedPositionKeys.add(`${status.match_event_id}:${status.league_configuration_position_id}`);
    }
  }
  const positionIds = [
    ...new Set([
      ...playedEvents.map((event) => event.league_configuration_position_id).filter(Boolean),
      ...cancellationEvents.map((event) => event.league_configuration_position_id).filter(Boolean),
      ...replacementEvents.map((event) => event.league_configuration_position_id).filter(Boolean),
      ...entries.map((entry) => entry.league_configuration_position_id),
      ...lineResults.map((result) => result.league_configuration_position_id),
    ]),
  ] as string[];
  const positionsResult = positionIds.length > 0
    ? await supabase.from('league_configuration_positions').select('id, position_code, line_type').in('id', positionIds).is('deleted_at', null)
    : { data: [], error: null };

  if (positionsResult.error) throw new Error(positionsResult.error.message);

  const positionsById = new Map(((positionsResult.data || []) as unknown as PositionRow[]).map((position) => [position.id, position]));
  const lineTypeByPosition = new Map(Array.from(positionsById.values()).map((position) => [position.id, position.line_type]));
  const matchById = new Map(matches.map((match) => [match.id, match]));
  const scheduledByEntryKey = new Map(scheduledEvents.map((event) => [
    `${event.match_event_id}:${event.player_id}:${event.league_configuration_position_id || ''}`,
    event,
  ]));
  const playedEventKeys = new Set(playedEvents.map((event) => `${event.match_event_id}:${event.player_id}`));
  // Administrative corrections/adjustments can coexist with a real appearance.
  // Only an actual cancellation should suppress its played/fallback count.
  const cancellationParticipationEvents = cancellationEvents.filter((event) => isParticipationCancellation(event.participation_event_type));
  const cancellationKeys = new Set(cancellationParticipationEvents.map((event) => `${event.match_event_id}:${event.player_id}:${event.league_configuration_position_id || ''}`));
  const replacementKeys = new Set(replacementEvents.map((event) => `${event.match_event_id}:${event.player_id}:${event.league_configuration_position_id || ''}`));
  const explicitParticipationKeys = new Set(
    [...playedEvents, ...cancellationParticipationEvents, ...replacementEvents].map((event) => `${event.match_event_id}:${event.player_id}:${event.league_configuration_position_id || ''}`),
  );
  const allocationOverrideKeys = new Set(
    [...playedEvents, ...cancellationEvents, ...replacementEvents]
      .filter((event) => event.allocation_count_indicator)
      .map((event) => `${event.match_event_id}:${event.player_id}:${event.league_configuration_position_id || ''}`),
  );
  const futureEntrySuppressionKeys = new Set([
    ...cancellationKeys,
    ...replacementKeys,
    ...allocationOverrideKeys,
    ...playedEvents.map((event) => `${event.match_event_id}:${event.player_id}:${event.league_configuration_position_id || ''}`),
  ]);
  const counts = new Map<string, {
    totalPlayed: number;
    provisionalPlayed: number;
    targetCountedPlayed: number;
    allocationCredits: number;
    lateCancellationCredits: number;
    plannedByMatch: Map<string, number>;
    lateCancels: number;
    lateSubstitutes: number;
    playedRegular: number;
    singlesPlayed: number;
    doublesPlayed: number;
    futureSingles: number;
    futureDoubles: number;
    playedDates: string[];
    futureDates: string[];
    homeMatches: number;
    awayMatches: number;
    neutralMatches: number;
    unknownSiteMatches: number;
  }>();
  const detailsByPlayer = new Map<string, ParticipationDetail[]>();
  const ensureCounts = (playerId: string) => {
    const existing = counts.get(playerId);
    if (existing) return existing;
    const created = {
      totalPlayed: 0,
      provisionalPlayed: 0,
      targetCountedPlayed: 0,
      allocationCredits: 0,
      lateCancellationCredits: 0,
      plannedByMatch: new Map<string, number>(),
      lateCancels: 0,
      lateSubstitutes: 0,
      playedRegular: 0,
      singlesPlayed: 0,
      doublesPlayed: 0,
      futureSingles: 0,
      futureDoubles: 0,
      playedDates: [] as string[],
      futureDates: [] as string[],
      homeMatches: 0,
      awayMatches: 0,
      neutralMatches: 0,
      unknownSiteMatches: 0,
    };
    counts.set(playerId, created);
    return created;
  };
  const addDetail = (playerId: string, detail: ParticipationDetail) => {
    detailsByPlayer.set(playerId, [...(detailsByPlayer.get(playerId) || []), detail]);
  };
  const detailBase = (matchId: string, positionId: string | null): Pick<ParticipationDetail, 'matchDate' | 'matchSiteType' | 'opponent' | 'lineAssignment'> => {
    const match = matchById.get(matchId);
    const position = positionId ? positionsById.get(positionId) : undefined;
    return {
      matchDate: match?.match_date || '',
      matchSiteType: match?.match_site_type || 'Unknown',
      opponent: match?.opponent_name || 'TBD',
      lineAssignment: position?.position_code || '-',
    };
  };
  const addLine = (playerId: string, matchId: string, positionId: string | null, bucket: 'played' | 'future', options: { countForTarget?: boolean } = {}) => {
    if (!positionId) return;
    const match = matchById.get(matchId);
    if (!match) return;
    const lineType = lineTypeByPosition.get(positionId);
    const playerCounts = ensureCounts(playerId);
    if (bucket === 'played') {
      playerCounts.totalPlayed += 1;
      if (options.countForTarget !== false) playerCounts.targetCountedPlayed += 1;
      playerCounts.playedDates.push(match.match_date);
      if (lineType === 'singles') playerCounts.singlesPlayed += 1;
      else playerCounts.doublesPlayed += 1;
    } else {
      if (options.countForTarget !== false) {
        playerCounts.plannedByMatch.set(matchId, (playerCounts.plannedByMatch.get(matchId) || 0) + 1);
        playerCounts.futureDates.push(match.match_date);
        if (lineType === 'singles') playerCounts.futureSingles += 1;
        else playerCounts.futureDoubles += 1;
      }
    }
    if (match.match_site_type === 'Home') playerCounts.homeMatches += 1;
    else if (match.match_site_type === 'Away') playerCounts.awayMatches += 1;
    else if (match.match_site_type === 'Neutral') playerCounts.neutralMatches += 1;
    else playerCounts.unknownSiteMatches += 1;
  };

  const currentParticipantKeys = new Set(entries.map((entry) => `${entry.match_event_id}:${entry.player_id}`));
  playedEvents.forEach((event) => {
    // Completion-generated events snapshot the lineup. A later lineup correction
    // supersedes that snapshot; official/manual participant evidence stays authoritative.
    if (event.participation_event_reason === 'Match completed'
      && !currentParticipantKeys.has(`${event.match_event_id}:${event.player_id}`)) return;
    const eventKey = `${event.match_event_id}:${event.player_id}:${event.league_configuration_position_id || ''}`;
    const explicitOverrideOwnsAllocation = replacementKeys.has(eventKey) || cancellationKeys.has(eventKey);
    const scheduled = scheduledByEntryKey.get(eventKey);
    const countsTowardAllocation = event.manual_allocation_override_indicator
      ? event.allocation_count_indicator
      : scheduled?.manual_allocation_override_indicator
        ? scheduled.allocation_count_indicator
        : event.allocation_count_indicator;
    const bucket = pastMatchIds.has(event.match_event_id) ? 'played' : futureMatchIds.includes(event.match_event_id) ? 'future' : null;
    if (!explicitOverrideOwnsAllocation && bucket && countsTowardAllocation) {
      addLine(event.player_id, event.match_event_id, event.league_configuration_position_id, bucket, { countForTarget: true });
    } else if (!explicitOverrideOwnsAllocation && bucket === 'played') {
      addLine(event.player_id, event.match_event_id, event.league_configuration_position_id, 'played', { countForTarget: false });
    }
    if (bucket && !replacementKeys.has(eventKey)) {
      if (bucket === 'played' && countsTowardAllocation && !explicitOverrideOwnsAllocation) ensureCounts(event.player_id).playedRegular += 1;
      addDetail(event.player_id, {
        id: `played:${eventKey}`,
        ...detailBase(event.match_event_id, event.league_configuration_position_id),
        participationStatus: bucket === 'future' ? 'Future Assignment' : 'Played',
        cancellationReason: '',
        replacementDetails: '',
      });
    }
  });
  entries.forEach((entry) => {
    const entryKey = `${entry.match_event_id}:${entry.player_id}:${entry.league_configuration_position_id}`;
    const defaultedLine = defaultedPositionKeys.has(`${entry.match_event_id}:${entry.league_configuration_position_id}`);
    const countsTowardAllocation = scheduledByEntryKey.get(entryKey)?.allocation_count_indicator !== false;
    if (pastMatchIds.has(entry.match_event_id) && !defaultedLine) {
      if (!playedEventKeys.has(`${entry.match_event_id}:${entry.player_id}`) && !cancellationKeys.has(entryKey) && !explicitParticipationKeys.has(entryKey)) {
        addLine(entry.player_id, entry.match_event_id, entry.league_configuration_position_id, 'played', { countForTarget: countsTowardAllocation });
        if (matchById.get(entry.match_event_id)?.match_status !== 'Completed') ensureCounts(entry.player_id).provisionalPlayed += 1;
        if (!replacementKeys.has(entryKey)) {
          if (countsTowardAllocation) ensureCounts(entry.player_id).playedRegular += 1;
          addDetail(entry.player_id, {
            id: `played-fallback:${entryKey}`,
            ...detailBase(entry.match_event_id, entry.league_configuration_position_id),
            participationStatus: 'Played',
            cancellationReason: '',
            replacementDetails: '',
          });
        }
      }
    } else if (futureMatchIds.includes(entry.match_event_id)) {
      if (!futureEntrySuppressionKeys.has(entryKey)) {
        addLine(entry.player_id, entry.match_event_id, entry.league_configuration_position_id, 'future', { countForTarget: countsTowardAllocation });
        addDetail(entry.player_id, {
          id: `future:${entryKey}`,
          ...detailBase(entry.match_event_id, entry.league_configuration_position_id),
          participationStatus: 'Future Assignment',
          cancellationReason: '',
          replacementDetails: '',
        });
      }
    }
  });
  cancellationEvents.forEach((event) => {
    const playerCounts = ensureCounts(event.player_id);
    if (event.allocation_count_indicator) playerCounts.allocationCredits += 1;
    if (event.allocation_count_indicator && event.participation_event_type === 'cancelled_late') playerCounts.lateCancellationCredits += 1;
    if (event.participation_event_type === 'cancelled_late') {
      playerCounts.lateCancels += 1;
      addDetail(event.player_id, {
        id: `late-cancel:${event.match_event_id}:${event.player_id}:${event.league_configuration_position_id || ''}`,
        ...detailBase(event.match_event_id, event.league_configuration_position_id),
        participationStatus: 'Late Cancel',
        cancellationReason: event.participation_event_reason || '',
        replacementDetails: event.replacement_player_id && playerLabel ? `Replacement: ${playerLabel(event.replacement_player_id)}` : '',
      });
    }
  });
  const substitutionAdjustments = new Map<string, number>();
  replacementEvents.forEach((event) => {
    const playerCounts = ensureCounts(event.player_id);
    const match = matchById.get(event.match_event_id);
    const isFutureMatch = Boolean(match && futureMatchIds.includes(event.match_event_id));
    playerCounts.lateSubstitutes += 1;
    if (event.allocation_count_indicator && isFutureMatch) {
      addLine(event.player_id, event.match_event_id, event.league_configuration_position_id, 'future');
    } else if (pastMatchIds.has(event.match_event_id) && (currentParticipantKeys.has(`${event.match_event_id}:${event.player_id}`) || playedEvents.some(played => played.match_event_id === event.match_event_id && played.player_id === event.player_id && played.participation_event_reason !== 'Match completed'))) {
      addLine(event.player_id, event.match_event_id, event.league_configuration_position_id, 'played', { countForTarget: event.allocation_count_indicator });
      if (!event.allocation_count_indicator) substitutionAdjustments.set(event.player_id, (substitutionAdjustments.get(event.player_id) || 0) - 1);
      if (match?.match_status !== 'Completed') playerCounts.provisionalPlayed += 1;
    }
    const status = event.participation_event_type === 'standby_promotion' || event.participation_event_notes?.toLowerCase().includes('standby') ? 'Standby Promotion' : 'Late Substitute';
    addDetail(event.player_id, {
      id: `late-sub:${event.match_event_id}:${event.player_id}:${event.league_configuration_position_id || ''}`,
      ...detailBase(event.match_event_id, event.league_configuration_position_id),
      participationStatus: status,
      cancellationReason: event.participation_event_reason || '',
      replacementDetails: event.replaced_player_id && playerLabel ? `Replaced: ${playerLabel(event.replaced_player_id)}` : '',
    });
  });

  const rows = visibleRosterRows.map<ParticipationReportRow>((roster) => {
    const player = playerById.get(roster.player_id);
    const playerCounts = counts.get(roster.player_id) || {
      totalPlayed: 0,
      provisionalPlayed: 0,
      targetCountedPlayed: 0,
      allocationCredits: 0,
      lateCancellationCredits: 0,
      plannedByMatch: new Map<string, number>(),
      lateCancels: 0,
      lateSubstitutes: 0,
      playedRegular: 0,
      singlesPlayed: 0,
      doublesPlayed: 0,
      futureSingles: 0,
      futureDoubles: 0,
      playedDates: [] as string[],
      futureDates: [] as string[],
      homeMatches: 0,
      awayMatches: 0,
      neutralMatches: 0,
      unknownSiteMatches: 0,
    };
    const totalFuture = playerCounts.futureSingles + playerCounts.futureDoubles;
    const targetMatches = isDesignatedSubstitute(roster) ? 0 : roster.target_match_count ?? 0;
    const adjustedTarget = targetMatches;
    const statusDelta = playerCounts.targetCountedPlayed + playerCounts.allocationCredits + totalFuture - adjustedTarget;
    const lastPlayedDate = playerCounts.playedDates.length > 0 ? [...playerCounts.playedDates].sort().at(-1) || null : null;
    const nextMatchDate = playerCounts.futureDates.length > 0 ? [...playerCounts.futureDates].sort()[0] || null : null;
    const totalCompletedAndFutureScheduledMatches = playerCounts.homeMatches + playerCounts.awayMatches + playerCounts.neutralMatches + playerCounts.unknownSiteMatches;
    const homeMatchPercentage = totalCompletedAndFutureScheduledMatches > 0
      ? Math.round((playerCounts.homeMatches / totalCompletedAndFutureScheduledMatches) * 100)
      : null;
    const plannedAssignments = [...playerCounts.plannedByMatch].map(([matchId, credits]) => ({
      matchId, credits, lineupStatus: (matchById.get(matchId)?.lineup_status === 'Draft' ? 'Draft' : 'Published') as 'Draft' | 'Published',
    }));
    return {
      playerId: roster.player_id,
      playerName: formatPlayerName(player, userPreferences) || 'Unknown Player',
      firstName: player?.first_name || '',
      lastName: player?.last_name || '',
      sex: player?.sex || null,
      targetMatches,
      adjustedTarget,
      totalPlayed: playerCounts.totalPlayed,
      countedPlayed: playerCounts.targetCountedPlayed,
      provisionalPlayed: playerCounts.provisionalPlayed,
      lateCancels: playerCounts.lateCancels,
      lateSubstitutes: playerCounts.lateSubstitutes,
      playedRegular: playerCounts.playedRegular,
      singlesPlayed: playerCounts.singlesPlayed,
      doublesPlayed: playerCounts.doublesPlayed,
      futureSingles: playerCounts.futureSingles,
      futureDoubles: playerCounts.futureDoubles,
      totalFuture,
      statusDelta,
      lastPlayedDate,
      lastPlayedWeeksAgo: lastPlayedDate ? wholeWeeksBetween(lastPlayedDate, today) : null,
      nextMatchDate,
      nextMatchWeeksAway: nextMatchDate ? wholeWeeksBetween(today, nextMatchDate) : null,
      homeMatches: playerCounts.homeMatches,
      awayMatches: playerCounts.awayMatches,
      neutralMatches: playerCounts.neutralMatches,
      unknownSiteMatches: playerCounts.unknownSiteMatches,
      totalCompletedAndFutureScheduledMatches,
      homeMatchPercentage,
      calculation: {
        lateCancellationAdjustment: playerCounts.lateCancellationCredits,
        otherAllocationAdjustment: playerCounts.allocationCredits - playerCounts.lateCancellationCredits + playerCounts.targetCountedPlayed - playerCounts.totalPlayed - (substitutionAdjustments.get(roster.player_id) || 0),
        // Start with actual appearances; show target-credit exclusions separately.
        lateSubstitutionAdjustment: substitutionAdjustments.get(roster.player_id) || 0,
        adjustedParticipation: statusDelta + adjustedTarget,
      },
      plannedAssignments,
      plannedPublished: plannedAssignments.filter((assignment) => assignment.lineupStatus === 'Published').reduce((sum, assignment) => sum + assignment.credits, 0),
      plannedDraft: plannedAssignments.filter((assignment) => assignment.lineupStatus === 'Draft').reduce((sum, assignment) => sum + assignment.credits, 0),
      details: (detailsByPlayer.get(roster.player_id) || []).sort((a, b) => a.matchDate.localeCompare(b.matchDate)),
    };
  });

  return { rows, seasonId: selectedSeasonId, nextBoundaryAt };
}
